import * as Tone from 'tone'
import { InstrumentBase } from './InstrumentBase';
import { getAudioGraphInstance } from '../audioEngine';
import { addKeysAsField } from '../utils/misc';
import { PianoWamUI } from '../projectViews/stationViews/PianoWamUI.jsx';
import { sendNoteOnToWamNode, sendNoteOffToWamNode, stopAllNotesForWamNode, createWamNode} from '../utils/wam.js';
import { PianoRoller } from './PianoRoller.js';
import colors from "../../styles/colors.module.scss";


export class PianoWam extends InstrumentBase {
    static metadata = {
        tipus:  'piano_wam',
        tipusDisplay:  'Piano Wam Legacy',
        versio:  '0.1',
        color:  colors.orange,
        hasLogo:  false,
        hasMiniature:  false,
    }
    static parametersDescription = addKeysAsField({
        ...super.parametersDescription,
        ...PianoRoller.parametersDescription,
        sound: {type: 'enum', options: ['Acústic', 'Elèctric 1', 'Elèctric 2'], initial: 'Acústic', bind: "preset"},
    }, "nom");
    static pitchRange = {
        ...super.pitchRange,
        typical: {min: 48, max: 71}
    }

    getUserInterfaceComponent() {
        return PianoWamUI
    }

    async buildInstrumentAudioGraph() {
        await super.buildInstrumentAudioGraph();
        const wamUrl = 'https://www.webaudiomodules.com/community/plugins/burns-audio/soundfont/index.js'
        const wamNode = await createWamNode(wamUrl, this.id);
        const gainCompensation = new Tone.Gain(Tone.dbToGain(12));
        // wamNode.getState().then((params) => console.debug(params));  // Print available WAM node parameters

        const gainOutput = new Tone.Gain(Tone.dbToGain(0));
        this.audioNodes = {
            gainOutput: gainOutput,
            gainCompensation: gainCompensation,
            wamNode: wamNode,
        }
        Tone.connect(this.audioNodes.wamNode, this.audioNodes.gainCompensation);
        Tone.connect(this.audioNodes.gainCompensation, this.audioNodes.gainOutput);

        // Crea els nodes d'efectes (això també els afegirà al diccionari de nodes de l'instrument)
        this.addEffectChainNodes(this.audioNodes.gainOutput, this.bus);
    }

    setParameterInAudioGraph(name, value) {
        switch (name) {
            case "sound":
                this.audioNodes.wamNode.setState({  // The soundfont WAM node doesn't use parameters for changing the sound, but a setState function, so we have to treat it differently
                    'instrument': {
                        'Acústic': 'acoustic_grand_piano', 
                        'Elèctric 1': 'electric_piano_1', 
                        'Elèctric 2': 'electric_piano_2'}[value] 
                })
                break;
        }
    }

    onSequencerStep(currentMainSequencerStep, time) {
        if (this.getPlaybackParameterValue("clipNumber") === undefined) return;
        if (!Object.keys(this.audioNodes).length) { return };
        
        // Iterate over all the notes in the sequence and trigger those that start in the current beat (step)
        const currentStep = currentMainSequencerStep % this.getPlaybackClip().length;
        const sequence = this.getPlaybackClip().sequence;
        for (let i = 0; i < sequence.length; i++) {
            const note = sequence[i];
            if (note.onset === currentStep) {
                const duration = Math.min(note.onset + note.duration, this.getPlaybackClip().length) - note.onset;
                sendNoteOnToWamNode(this.audioNodes.wamNode, note.pitch, (note.velocity ?? 127), time);
                sendNoteOffToWamNode(this.audioNodes.wamNode, note.pitch, 0, time + duration * getAudioGraphInstance().get16BeatTime());
            }
        }   
    }
    
    onTransportStop() {
        if (!Object.keys(this.audioNodes).length) { return };
        stopAllNotesForWamNode(this.audioNodes.wamNode, Tone.getContext().currentTime);
    }
    
    onStopAllSounds() {
        if (!Object.keys(this.audioNodes).length) { return };
        stopAllNotesForWamNode(this.audioNodes.wamNode, Tone.getContext().currentTime);
    }
    
    onMidiNote(pitch, midiVelocity=127, noteOff, extras) {
        if (!getAudioGraphInstance().isGraphBuilt()) return; 
        if (!Object.keys(this.audioNodes).length) { return };
        if (!noteOff) {
            sendNoteOnToWamNode(this.audioNodes.wamNode, pitch, midiVelocity, Tone.getContext().currentTime);
        } else {
            sendNoteOffToWamNode(this.audioNodes.wamNode, pitch, midiVelocity, Tone.getContext().currentTime);
        }
        if (!extras.skipRecording) this.handlePianoRollRecording(pitch, midiVelocity, noteOff);
    }
}